import { useState } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";
import type { SocialLink } from "./SocialsList";

export function useSocialsDraft(
  editingSocial: Partial<SocialLink> | null,
  setEditingSocial: React.Dispatch<React.SetStateAction<Partial<SocialLink> | null>>,
  socials: SocialLink[],
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>
) {
  const handleSaveDraft = async () => {
    if (!editingSocial) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: "social",
        item_id: editingSocial.name,
        draft_data: editingSocial
      });
      setActiveDraft(editingSocial);
      toast.success("Social draft saved to staging successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingSocial) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "social",
        item_id: editingSocial.name
      });
      setActiveDraft(null);
      const live = socials.find((s) => s.name === editingSocial.name);
      setEditingSocial(live || null);
      toast.success("Staged draft discarded successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

export function useSocialsManager(socials: SocialLink[], onRefresh: () => void) {
  const [editingSocial, setEditingSocial] = useState<Partial<SocialLink> | null>(null);
  const [activeDraft, setActiveDraft] = useState<any>(null);

  const { handleSaveDraft, handleDiscardDraft } = useSocialsDraft(
    editingSocial,
    setEditingSocial,
    socials,
    activeDraft,
    setActiveDraft
  );

  const startAdd = () => {
    setActiveDraft(null);
    setEditingSocial({
      name: "",
      url: "",
      icon: "link",
      color: "hover:text-brand-primary",
      sort_order: socials.length
    });
  };

  const handleEdit = (social: SocialLink) => {
    setEditingSocial(social);
    adminFetch(`/api/admin/drafts?item_type=social&item_id=${social.name}`)
      .then((res) => {
        if (res?.success && res.draft) {
          setActiveDraft(res.draft);
          setEditingSocial(res.draft);
          toast.info("Unpublished staged draft loaded for editing.");
        } else {
          setActiveDraft(null);
        }
      })
      .catch(() => setActiveDraft(null));
  };

  const handleSave = async () => {
    if (!editingSocial?.name || !editingSocial?.url) {
      toast.error("Name and URL are required!");
      return;
    }
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "social",
        item_id: editingSocial.name,
        draft_data: editingSocial
      });
      toast.success("Social link saved successfully!");
      setEditingSocial(null);
      setActiveDraft(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save social link");
    }
  };

  const handleDelete = async (name: string) => {
    if (!confirm("Are you sure you want to delete this social link?")) return;
    try {
      await adminFetch("/api/admin/socials", { operation: "delete", name });
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "social",
        item_id: name
      });
      toast.success("Social link deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete social link");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const sortedSocials = [...socials].sort((a, b) => a.sort_order - b.sort_order);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sortedSocials.length) return;

    const socialA = sortedSocials[index];
    const socialB = sortedSocials[swapIndex];
    if (!socialA || !socialB) return;

    try {
      await adminFetch("/api/admin/socials", { ...socialA, sort_order: socialB.sort_order });
      await adminFetch("/api/admin/socials", { ...socialB, sort_order: socialA.sort_order });
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to reorder links");
    }
  };

  return {
    editingSocial,
    setEditingSocial,
    activeDraft,
    setActiveDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove
  };
}

export default useSocialsManager;
